import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Dispatch, FC, Fragment, SetStateAction, useCallback } from 'react'

import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import { GalleryFloatingToolBar } from 'components/common/Gallery/GalleryFloatingToolBar'
import { MUIButton } from 'components/common/MUIButton'
import { ToolbarMode } from '../_main'
import { useClientProperty } from 'components/pages/Property/ClientProperty/_main/contexts'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

// enum UploadTypes {
//   PHOTO = 'PHOTO',
//   FLOOR_PLAN = 'FLOOR_PLAN',
//   VIDEO = 'VIDEO',
// }

/**
 * @interface Props
 */
interface Props {
  /** Mode of the floating bar */
  mode?: ToolbarMode
  /** Whether the download feature is available */
  isDownload?: boolean
  /** Whether the toolbar is open */
  isToolBarOpen: boolean
  /** Function to set the toolbar open state */
  setIsToolBarOpen?: Dispatch<SetStateAction<boolean>>
  /** Function to set the upload popup open state */
  setIsUploadPopupOpened: Dispatch<SetStateAction<boolean>>
  /** Function to select all purchased visuals */
  selectAllPurchased?: () => void
}

/**
 * Client gallery upload floating toolbar.
 * 
 * @example <ClientGalleryUploadFloatingToolBar />
 */
export const ClientGalleryUploadFloatingToolBar: FC<Props> = ({
  mode,
  isDownload = true,
  isToolBarOpen,
  setIsToolBarOpen,
  setIsUploadPopupOpened,
  selectAllPurchased
}) => {
  const { t } = useTranslation(['gallery'])
  const { spawnWarningToast } = useSnackbar()
  const { baseUserData, currentUserWorkspace } = useUserData()
  const {
    sessionId,
    getCategoryConfig,
    propertyData,
    propertyId
  } = useClientProperty()

  // Hide the getUploadTypeIcon for now since only PHOTO upload is implemented
  // const getUploadTypeIcon = useCallback((type: UploadTypes) => {
  //   switch (type) {
  //     case UploadTypes.PHOTO:
  //       return getProductKindIcon(ProductKind.GROUND_PHOTO)
  //     case UploadTypes.FLOOR_PLAN:
  //       return getProductKindIcon(ProductKind.FLOOR_PLAN_EDITING_2D)
  //     case UploadTypes.VIDEO:
  //       return getProductKindIcon(ProductKind.STANDARD_GROUND_VIDEO)
  //     default:
  //       return getProductKindIcon(ProductKind.GROUND_PHOTO)
  //   }
  // }, [])

  const hadleOpenUploadPopup = useCallback(() => {
    logAnalyticsEvent(AnalyticsEvent.SELF_UPLOAD_CLICK, {
      propertyName: propertyData?.title,
      propertyId,
      realtyId: propertyId,
      planId: currentUserWorkspace?.id,
    })

    if (!sessionId) {
      getCategoryConfig.mutate({ email: baseUserData?.email },
        {
          onSuccess: () => {
            setIsUploadPopupOpened(true)
          },
          onError: (error) => {
            spawnWarningToast(t('open_self_upload_error'))
          }
        }
      )
      return
    }

    setIsUploadPopupOpened(true)
  }, [baseUserData?.email, getCategoryConfig, propertyData?.title, propertyId, sessionId, setIsUploadPopupOpened, spawnWarningToast, t, currentUserWorkspace?.id])

  return (
    <GalleryFloatingToolBar
      width="auto"
      isToolBarOpen={isToolBarOpen && mode === ToolbarMode.UPLOAD_VISUALS}
      openToolBarButtonLabel={t('select_visuals')}
      onOpenToolBar={() => setIsToolBarOpen?.(true)}
      contentActionContainer={
        <Fragment>

          <MUIButton
            type="darkSecondaryNoborder"
            startIcon={<CloudUploadOutlinedIcon fontSize="large" />}
            onClick={hadleOpenUploadPopup}
          >
            {t('upload_visuals_button')}
          </MUIButton>

          {/* Hide the dropdown list since only the PHOTO upload is implemented at this stage */}
          {/* <MUIDropdown
              button={(isOpen, action) => (
                <MUIButton
                  type="darkSecondaryNoborder"
                  startIcon={<CloudUploadOutlinedIcon fontSize="large" />}
                  onClick={action}
                >
                  {t('upload_visuals_button')}
                </MUIButton>
              )}
            >
              {Object.values(UploadTypes).map((type) => (
                <MUIDropdownItem key={type} onClick={() => setIsUploadPopupOpened(true)}>
                  <Stack gap=".8rem" alignItems="center" flexDirection="row">
                    <CircleIcon
                      size="2.4rem"
                      circleColor={BEIGE_600}
                      icon={getUploadTypeIcon(type)}
                    />
                    <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                      {t(`upload_visuals_type.${type}`)}
                    </Typography>
                  </Stack>
                </MUIDropdownItem>
              ))}
            </MUIDropdown> */}

          {isDownload &&
            <MUIButton
              type="darkPrimary"
              startIcon={<CloudDownloadOutlinedIcon fontSize="large" />}
              onClick={() => {
                selectAllPurchased?.()
                setIsToolBarOpen?.(true)
              }}
            >
              {t('download_visuals')}
            </MUIButton>
          }

        </Fragment>
      }

    />
  )
}
